import React, { useEffect, useRef, useState } from 'react';
import { Alert, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaCalendarAlt, FaChartBar, FaClipboardList, FaCompress, FaExpand, FaFilter, FaMapMarkerAlt, FaSitemap } from 'react-icons/fa';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis, YAxis
} from 'recharts';

function AuditProgress() {
  const [auditData, setAuditData] = useState({
    audits: [],
    branchData: [],
    departmentData: []
  });
  const [filterOptions, setFilterOptions] = useState({
    branches: [],
    departments: [],
    audits: [],
    dateRange: {}
  });
  const [filters, setFilters] = useState({
    branches: '',
    departments: '',
    audits: '',
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({});
  const [viewMode, setViewMode] = useState('branch');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFilterChanging, setIsFilterChanging] = useState(false);
  const [previousFilters, setPreviousFilters] = useState(null);
  const containerRef = useRef(null);

  const BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData || !userData.BranchCode) {
          throw new Error('User data not found');
        }

        const response = await fetch(`${BASE_URL}/get-audit-progress-filters/${userData.BranchCode}`);
        if (!response.ok) {
          throw new Error('Failed to fetch filter options');
        }

        const data = await response.json();
        if (data.success) {
          setFilterOptions(data.filters);
        }
      } catch (err) {
        console.error('Error fetching filter options:', err);
        setError(err.message);
      }
    };

    fetchFilterOptions();
  }, [BASE_URL]);

  useEffect(() => {
    const fetchAuditProgress = async () => {
      try {
        setLoading(true);
        const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData || !userData.BranchCode) {
          throw new Error('User data not found');
        }

        const params = new URLSearchParams();
        if (filters.branches) params.append('branches', filters.branches);
        if (filters.departments) params.append('departments', filters.departments);
        if (filters.audits) params.append('audits', filters.audits);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);

        const response = await fetch(
          `${BASE_URL}/get-audit-progress/${userData.BranchCode}?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch audit progress data');
        }

        const data = await response.json();
        if (data.success) {
          setAuditData({
            audits: data.data.audits || [],
            branchData: data.data.branchData || [],
            departmentData: data.data.departmentData || []
          });
          setSummary(data.data.summary || {});
          
          if (filters.branches) {
            setViewMode('department');
          } else {
            setViewMode('branch');
          }
        } else {
          throw new Error(data.error || 'Failed to fetch audit progress data');
        }
      } catch (err) {
        console.error('Error fetching audit progress:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditProgress();
  }, [BASE_URL, filters]);

  const handleFilterChange = (filterType, value) => {
    setIsFilterChanging(true);
    setPreviousFilters(filters);
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: value
      };
      
      if (filterType === 'branches' && !value) {
        newFilters.departments = '';
      }
      
      return newFilters;
    });
    
    setTimeout(() => setIsFilterChanging(false), 100);
  };

  const clearFilter = (filterType) => {
    setIsFilterChanging(true);
    setPreviousFilters(filters);
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: ''
      };
      
      if (filterType === 'branches') {
        newFilters.departments = '';
      }
      
      return newFilters;
    });
    
    setTimeout(() => setIsFilterChanging(false), 100);
  };

  const clearAllFilters = () => {
    setIsFilterChanging(true);
    setPreviousFilters(filters);
    
    setFilters({
      branches: '',
      departments: '',
      audits: '',
      startDate: '',
      endDate: ''
    });
    setViewMode('branch');
    
    setTimeout(() => setIsFilterChanging(false), 100);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const preventFullscreenExit = (e) => {
    if (isFullscreen && e.target.closest('.filter-section')) {
      e.stopPropagation();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Fullscreen changed:', isCurrentlyFullscreen, 'Current filters:', filters);
      }
    };

    const handleFullscreenError = (e) => {
      console.error('Fullscreen error event:', e);
      setIsFullscreen(false);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    document.addEventListener('fullscreenerror', handleFullscreenError);
    document.addEventListener('webkitfullscreenerror', handleFullscreenError);
    document.addEventListener('mozfullscreenerror', handleFullscreenError);
    document.addEventListener('MSFullscreenError', handleFullscreenError);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      
      document.removeEventListener('fullscreenerror', handleFullscreenError);
      document.removeEventListener('webkitfullscreenerror', handleFullscreenError);
      document.removeEventListener('mozfullscreenerror', handleFullscreenError);
      document.removeEventListener('MSFullscreenError', handleFullscreenError);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isFullscreen && e.key === 'Escape') {
        const activeElement = document.activeElement;
        if (activeElement && (
          activeElement.tagName === 'SELECT' ||
          activeElement.tagName === 'INPUT' ||
          activeElement.closest('.filter-section')
        )) {
          e.preventDefault();
          e.stopPropagation();
          activeElement.blur();
          return false;
        }
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown, { capture: true });
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [isFullscreen]);

  const getCurrentDisplayData = () => {
    let data;
    if (viewMode === 'department') {
      if (filters.departments) {
        data = auditData.departmentData.filter(dept => dept.departmentName === filters.departments);
      } else {
        data = auditData.departmentData;
      }
    } else {
      data = auditData.branchData;
    }
    
    return data.map(item => ({
      ...item,
      progress: item.avgScore,
      remaining: 100 - item.avgScore
    }));
  };

  const getFilteredDepartments = () => {
    if (!filters.branches) {
      return filterOptions.departments;
    }
    
    const branchAudits = auditData.audits.filter(audit => audit.branchId == filters.branches);
    const branchDepartments = [...new Set(branchAudits.flatMap(audit => audit.departments))];
    
    return filterOptions.departments.filter(dept => branchDepartments.includes(dept.name));
  };

  const currentData = getCurrentDisplayData();
  
  const getScoreColor = (score) => {
    if (score >= 80) return '#28a745';
    if (score >= 60) return '#ffc107';
    return '#dc3545';
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
          return new Date(dateString).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          });
        } catch (error) {
          return 'Invalid Date';
        }
      };

      const isMobile = window.innerWidth < 768;
      const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;

      return (
        <div className="custom-tooltip" style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          padding: isMobile ? '12px' : '16px',
          border: '1px solid rgba(0,0,0,0.08)',
          borderRadius: isMobile ? '8px' : '12px',
          boxShadow: isMobile ? '0 4px 16px rgba(0,0,0,0.1)' : '0 8px 32px rgba(0,0,0,0.12)',
          minWidth: isMobile ? '180px' : isTablet ? '200px' : '220px',
          maxWidth: isMobile ? '280px' : '320px',
          backdropFilter: 'blur(10px)',
          fontFamily: '"Segoe UI", "Roboto", sans-serif',
          zIndex: 1000
        }}>
          <p className="fw-bold mb-2 mb-md-3" style={{ 
            color: '#1a2b42', 
            fontSize: isMobile ? '13px' : isTablet ? '14px' : '15px',
            letterSpacing: '0.3px',
            borderBottom: '2px solid #e9ecef',
            paddingBottom: isMobile ? '6px' : '8px',
            lineHeight: 1.3,
            wordBreak: 'break-word'
          }}>
            {viewMode === 'department' ? data.departmentName : data.branchName}
          </p>
          
          {data.startDate && (
            <p className="mb-1 mb-md-2 d-flex align-items-center" style={{ 
              color: '#495057',
              fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
              margin: isMobile ? '4px 0' : '6px 0',
              fontWeight: '500',
              flexWrap: 'wrap'
            }}>
              <FaCalendarAlt className="me-1 me-md-2 flex-shrink-0" size={isMobile ? 10 : 12} style={{ color: '#6c757d' }} />
              <span style={{ wordBreak: 'break-word' }}>Start Date: {formatDate(data.startDate)}</span>
            </p>
          )}
          
          <div className="mb-1 mb-md-2" style={{ 
            background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
            color: 'white',
            padding: isMobile ? '6px 8px' : '8px 12px',
            borderRadius: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Progress: {data.progress}%
          </div>
          
          <div className="mb-2 mb-md-3" style={{ 
            background: 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)',
            color: 'white',
            padding: isMobile ? '6px 8px' : '8px 12px',
            borderRadius: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '11px' : isTablet ? '12px' : '13px',
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Remaining: {data.remaining}%
          </div>
          
          <div style={{ 
            borderTop: '1px solid #e9ecef',
            paddingTop: isMobile ? '6px' : '8px'
          }}>
            
            
            {viewMode === 'department' && (
              <p className="mb-0" style={{ 
                color: '#495057',
                fontSize: isMobile ? '10px' : isTablet ? '11px' : '12px',
                fontWeight: '500',
                lineHeight: 1.4,
                wordBreak: 'break-word'
              }}>
                Total Score: <span style={{ fontWeight: '700', color: '#1a2b42' }}>{data.totalScore}/{data.totalPossibleScore}</span>
              </p>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const ChartLegend = () => {
    const isMobile = window.innerWidth < 768;
    
    return (
      <div className={`d-flex justify-content-center align-items-center mt-2 mt-md-3 mb-2 ${isMobile ? 'flex-column gap-2' : 'gap-4'}`}>
        <div className={`d-flex align-items-center ${isMobile ? 'flex-wrap justify-content-center gap-3' : 'gap-4'}`}>
          <div className="d-flex align-items-center">
            <div 
              className="me-2"
              style={{
                width: isMobile ? '14px' : '16px',
                height: isMobile ? '14px' : '16px',
                backgroundColor: '#28a745',
                borderRadius: '3px',
                border: '1px solid rgba(40, 167, 69, 0.3)',
                flexShrink: 0
              }}
            ></div>
            <span style={{ 
              fontSize: isMobile ? '11px' : '13px', 
              fontWeight: '500', 
              color: '#495057',
              fontFamily: '"Segoe UI", "Roboto", sans-serif'
            }}>
              Progress Completed
            </span>
          </div>
          <div className="d-flex align-items-center">
            <div 
              className="me-2"
              style={{
                width: isMobile ? '14px' : '16px',
                height: isMobile ? '14px' : '16px',
                backgroundColor: '#dc3545',
                borderRadius: '3px',
                border: '1px solid rgba(220, 53, 69, 0.3)',
                flexShrink: 0
              }}
            ></div>
            <span style={{ 
              fontSize: isMobile ? '11px' : '13px', 
              fontWeight: '500', 
              color: '#495057',
              fontFamily: '"Segoe UI", "Roboto", sans-serif'
            }}>
              Progress Remaining
            </span>
          </div>
        </div>
      </div>
    );
  };

  const FilterSection = ({ compact = false }) => (
    <Card 
      className={`border-0 shadow-sm rounded-3 overflow-hidden ${compact ? 'mb-3' : 'mb-3'} filter-section`}
      onClick={preventFullscreenExit}
      onMouseDown={preventFullscreenExit}
      onTouchStart={preventFullscreenExit}
    >
      <Card.Header className="bg-white border-bottom-0 py-2 py-md-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-2 text-primary">
              <FaFilter />
            </div>
            <h6 className="mb-0 fw-bold" style={{ fontSize: window.innerWidth < 768 ? '0.9rem' : '1rem' }}>
              Filter Audit Data {isFullscreen && <span className="badge bg-primary ms-2">Fullscreen Mode</span>}
            </h6>
          </div>
        </div>
      </Card.Header>
      <Card.Body className="pt-0" style={{ padding: window.innerWidth < 768 ? '0.75rem' : '1rem' }}>
        <Row className="g-2 g-md-3">
          <Col xs={12} sm={6} md={compact ? 2 : 6} lg={compact ? 2 : 2} xl={compact ? 2 : 2}>
            <Form.Group>
              <Form.Label className="small fw-semibold text-muted d-flex align-items-center mb-1 mb-md-2">
                <FaClipboardList className="me-1" size={window.innerWidth < 768 ? 10 : 12} /> 
                <span style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.8rem' }}>Audit</span>
              </Form.Label>
              <Form.Select
                size={window.innerWidth < 768 ? "sm" : "sm"}
                value={filters.audits}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterChange('audits', e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                className="border-0 bg-light"
                style={{ 
                  padding: window.innerWidth < 768 ? '0.4rem 0.75rem' : '0.5rem 0.75rem',
                  fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                  borderRadius: '8px',
                  height: window.innerWidth < 768 ? '36px' : '38px'
                }}
              >
                <option value="">All Audits</option>
                {filterOptions.audits?.map(audit => (
                  <option key={audit.id} value={audit.id}>{audit.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col xs={12} sm={6} md={compact ? 2 : 6} lg={compact ? 2 : 3} xl={compact ? 2 : 3}>
            <Form.Group className="position-relative">
              <Form.Label className="small fw-semibold text-muted d-flex align-items-center mb-1 mb-md-2">
                <FaMapMarkerAlt className="me-1" size={window.innerWidth < 768 ? 10 : 12} /> 
                <span style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.8rem' }}>Branch</span>
              </Form.Label>
              <Form.Select
                size={window.innerWidth < 768 ? "sm" : "sm"}
                value={filters.branches}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterChange('branches', e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                className="border-0 bg-light"
                style={{ 
                  padding: window.innerWidth < 768 ? '0.4rem 0.75rem' : '0.5rem 0.75rem',
                  fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                  borderRadius: '8px',
                  height: window.innerWidth < 768 ? '36px' : '38px'
                }}
              >
                <option value="">All Branches</option>
                {filterOptions.branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col xs={12} sm={6} md={compact ? 2 : 6} lg={compact ? 2 : 3} xl={compact ? 2 : 3}>
            <Form.Group>
              <Form.Label className="small fw-semibold text-muted d-flex align-items-center mb-1 mb-md-2">
                <FaSitemap className="me-1" size={window.innerWidth < 768 ? 10 : 12} /> 
                <span style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.8rem' }}>Department</span>
              </Form.Label>
              <Form.Select
                size={window.innerWidth < 768 ? "sm" : "sm"}
                value={filters.departments}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterChange('departments', e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                className="border-0 bg-light"
                disabled={!filters.branches}
                style={{ 
                  padding: window.innerWidth < 768 ? '0.4rem 0.75rem' : '0.5rem 0.75rem',
                  fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                  borderRadius: '8px',
                  height: window.innerWidth < 768 ? '36px' : '38px',
                  opacity: !filters.branches ? 0.6 : 1
                }}
              >
                <option value="">
                  {filters.branches ? 'All Departments' : 'Select Branch First'}
                </option>
                {getFilteredDepartments().map(dept => (
                  <option key={dept.id} value={dept.name}>{dept.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col xs={12} sm={6} md={compact ? 3 : 6} lg={compact ? 3 : 2} xl={compact ? 3 : 2}>
            <Form.Group>
              <Form.Label className="small fw-semibold text-muted d-flex align-items-center mb-1 mb-md-2">
                <FaCalendarAlt className="me-1" size={window.innerWidth < 768 ? 10 : 12} /> 
                <span style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.8rem' }}>Start Date</span>
              </Form.Label>
              <Form.Control
                type="date"
                size={window.innerWidth < 768 ? "sm" : "sm"}
                value={filters.startDate}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterChange('startDate', e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                className="border-0 bg-light"
                placeholder="Start Date"
                style={{ 
                  padding: window.innerWidth < 768 ? '0.4rem 0.75rem' : '0.5rem 0.75rem',
                  fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                  borderRadius: '8px',
                  height: window.innerWidth < 768 ? '36px' : '38px'
                }}
              />
            </Form.Group>
          </Col>
          
          <Col xs={12} sm={6} md={compact ? 3 : 6} lg={compact ? 3 : 2} xl={compact ? 3 : 2}>
            <Form.Group>
              <Form.Label className="small fw-semibold text-muted d-flex align-items-center mb-1 mb-md-2">
                <FaCalendarAlt className="me-1" size={window.innerWidth < 768 ? 10 : 12} /> 
                <span style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.8rem' }}>End Date</span>
              </Form.Label>
              <Form.Control
                type="date"
                size={window.innerWidth < 768 ? "sm" : "sm"}
                value={filters.endDate}
                onChange={(e) => {
                  e.stopPropagation();
                  handleFilterChange('endDate', e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                className="border-0 bg-light"
                placeholder="End Date"
                style={{ 
                  padding: window.innerWidth < 768 ? '0.4rem 0.75rem' : '0.5rem 0.75rem',
                  fontSize: window.innerWidth < 768 ? '0.8rem' : '0.875rem',
                  borderRadius: '8px',
                  height: window.innerWidth < 768 ? '36px' : '38px'
                }}
              />
            </Form.Group>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <Card className="border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading audit progress data...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
        <Card.Body>
          <Alert variant="danger">
            <Alert.Heading>Error Loading Audit Progress</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const hasActiveFilters = filters.branches || filters.departments || filters.audits || filters.startDate || filters.endDate;

  return (
    <div ref={containerRef} className={isFullscreen ? 'fullscreen-container' : ''}>
      <style jsx>{`
        .fullscreen-container {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 16px;
          height: 100vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
        }
        .fullscreen-chart {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }
        .fullscreen-chart .card {
          flex: 1;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1) !important;
          min-height: 0;
        }
        .fullscreen-chart .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 1rem !important;
          min-height: 0;
        }
        .fullscreen-chart .chart-container {
          flex: 1;
          min-height: 0;
        }
        .fullscreen-filters {
          flex-shrink: 0;
          margin-bottom: 1rem;
        }
        .fullscreen-filters .card {
          margin-bottom: 0 !important;
        }
        .card {
          box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
          border: 1px solid rgba(0,0,0,0.04) !important;
        }
        .card-header {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
          border-bottom: 1px solid rgba(0,0,0,0.06) !important;
        }
        .recharts-cartesian-grid line {
          stroke: rgba(0,0,0,0.05) !important;
          stroke-width: 1 !important;
        }
        .recharts-text {
          font-family: "Segoe UI", "Roboto", sans-serif !important;
          font-weight: 500 !important;
        }
        .recharts-bar:hover {
          filter: brightness(1.05);
          transition: filter 0.2s ease;
        }
        
        @media (max-width: 768px) {
          .fullscreen-container {
            padding: 8px;
          }
          
          .section-title {
            font-size: 1.1rem !important;
          }
          
          .card-header h6 {
            font-size: 0.9rem !important;
          }
          
          .btn-sm {
            font-size: 0.75rem !important;
            padding: 0.2rem 0.4rem !important;
          }
          
          .form-label {
            font-size: 0.75rem !important;
            margin-bottom: 0.25rem !important;
          }
          
          .form-select, .form-control {
            font-size: 0.8rem !important;
            padding: 0.4rem 0.75rem !important;
            height: 36px !important;
          }
          
          .row {
            margin: 0 -0.25rem !important;
          }
          
          .row > * {
            padding-right: 0.25rem !important;
            padding-left: 0.25rem !important;
          }
        }
        
        @media (min-width: 768px) and (max-width: 1024px) {
          .fullscreen-container {
            padding: 12px;
          }
          
          .form-select, .form-control {
            font-size: 0.875rem !important;
            padding: 0.5rem 0.75rem !important;
            height: 38px !important;
          }
        }
        
        @media (min-width: 1400px) {
          .container-fluid {
            max-width: 1600px;
            margin: 0 auto;
          }
        }
        
        @media (max-width: 768px) {
          .form-select, .form-control, .btn {
            min-height: 44px !important;
            touch-action: manipulation;
          }
          
          .form-select:focus, .form-control:focus {
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25) !important;
            border-color: rgba(13, 110, 253, 0.5) !important;
          }
        }
        
        @media (max-width: 768px) {
          input[type="date"] {
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M1 4h14M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4M1 4V2a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v2M5 2v2M11 2v2'/%3e%3c/svg%3e");
            background-repeat: no-repeat;
            background-position: right 0.75rem center;
            background-size: 16px;
            padding-right: 2.5rem;
          }
        }
      `}</style>
      
      {isFullscreen ? (
        <div className="fullscreen-chart">
          <div className="d-flex align-items-center justify-content-between mb-3 flex-shrink-0 flex-wrap">
            <h5 className="mb-2 mb-md-0 d-flex align-items-center">
              <div className="p-2 me-2">
                <FaChartBar />
              </div>
              <span>Audit Progress Analytics</span>
            </h5>
            <button
              className="btn btn-outline-primary btn-sm d-flex align-items-center"
              onClick={toggleFullscreen}
              title="Exit Fullscreen"
            >
              <FaCompress className="me-1" />
              <span className="d-none d-sm-inline">Exit Fullscreen</span>
              <span className="d-sm-none">Exit</span>
            </button>
          </div>

          <div className="fullscreen-filters">
            <FilterSection compact={true} />
          </div>
          
          <Card className="border-0 shadow-sm rounded-3 overflow-hidden flex-fill d-flex flex-column">
            <Card.Body className="flex-fill d-flex flex-column p-2 p-md-3">
              {currentData.length > 0 ? (
                <div className="flex-fill d-flex flex-column" style={{ minHeight: '400px' }}>
                  <div className="flex-fill">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={currentData} 
                        margin={{ 
                          top: 40, 
                          right: window.innerWidth < 768 ? 20 : 50, 
                          left: window.innerWidth < 768 ? 20 : 50, 
                          bottom: 60 
                        }}
                        style={{ fontFamily: '"Segoe UI", "Roboto", sans-serif' }}
                      >
                        <CartesianGrid 
                          strokeDasharray="2 4" 
                          vertical={false} 
                          stroke="rgba(0,0,0,0.06)"
                          strokeWidth={1}
                        />
                        <XAxis 
                          dataKey={viewMode === 'department' ? "departmentName" : "branchName"} 
                          angle={window.innerWidth < 768 ? -90 : -45} 
                          textAnchor="end"
                          height={60}
                          tick={{ 
                            fontSize: window.innerWidth < 768 ? 10 : 13, 
                            fill: '#495057',
                            fontWeight: '500',
                            fontFamily: '"Segoe UI", "Roboto", sans-serif'
                          }}
                          tickLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                          axisLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                          label={{ 
                            value: viewMode === 'department' ? 'Departments' : 'Branches', 
                            position: 'insideBottom', 
                            offset: -25,
                            style: { 
                              textAnchor: 'middle', 
                              fontSize: window.innerWidth < 768 ? '12px' : '14px', 
                              fill: '#495057',
                              fontWeight: '600'
                            }
                          }}
                        />
                        <YAxis 
                          domain={[0, 100]}
                          tickFormatter={(value) => `${value}%`}
                          tick={{ 
                            fontSize: window.innerWidth < 768 ? 10 : 13, 
                            fill: '#495057',
                            fontWeight: '500',
                            fontFamily: '"Segoe UI", "Roboto", sans-serif'
                          }}
                          axisLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                          tickLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                          label={{ 
                            value: 'Progress (%)', 
                            angle: -90, 
                            position: 'insideLeft',
                            style: { 
                              textAnchor: 'middle', 
                              fontSize: window.innerWidth < 768 ? '12px' : '14px', 
                              fill: '#495057',
                              fontWeight: '600'
                            }
                          }}
                        />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Bar 
                          dataKey="progress" 
                          stackId="a"
                          fill="#28a745"
                          name="Progress"
                          barSize={window.innerWidth < 768 ? 60 : 100}
                          radius={[0, 0, 0, 0]}
                          animationDuration={1800}
                          animationEasing="ease-out"
                        >
                          <LabelList 
                            dataKey="progress" 
                            position="center" 
                            formatter={(value) => value > 6 ? `${value}%` : ''}
                            style={{ 
                              fontSize: window.innerWidth < 768 ? '10px' : '12px', 
                              fill: 'white', 
                              fontWeight: '700',
                              fontFamily: '"Segoe UI", "Roboto", sans-serif',
                              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                            }} 
                          />
                        </Bar>
                        <Bar 
                          dataKey="remaining" 
                          stackId="a"
                          fill="#dc3545"
                          name="Remaining"
                          barSize={window.innerWidth < 768 ? 60 : 100}
                          radius={[4, 4, 0, 0]}
                          animationDuration={1800}
                          animationEasing="ease-out"
                        >
                          <LabelList 
                            dataKey="remaining" 
                            position="center" 
                            formatter={(value) => value > 6 ? `${value}%` : ''}
                            style={{ 
                              fontSize: window.innerWidth < 768 ? '10px' : '12px', 
                              fill: 'white', 
                              fontWeight: '700',
                              fontFamily: '"Segoe UI", "Roboto", sans-serif',
                              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                            }} 
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <ChartLegend />
                </div>
              ) : (
                <div className="text-center py-3 py-md-5 flex-fill d-flex flex-column justify-content-center">
                  <div className="mb-3 mb-md-4 bg-light p-3 p-md-4 rounded-circle d-inline-flex align-items-center justify-content-center" style={{
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '2px solid #dee2e6'
                  }}>
                    <FaChartBar size={window.innerWidth < 768 ? 24 : 36} className="text-secondary" />
                  </div>
                  <h6 className="text-muted mb-2 mb-md-3" style={{ fontWeight: '600' }}>No audit progress data available</h6>
                  <p className="text-muted small" style={{ lineHeight: '1.6' }}>
                    {hasActiveFilters
                      ? 'No data found for the selected filters. Try adjusting your filters to see more data.'
                      : 'Complete some audits to see progress analytics here.'}
                  </p>
                  {hasActiveFilters && (
                    <button 
                      className="btn btn-outline-primary btn-sm mt-2 mt-md-3 px-3 px-md-4 py-2"
                      onClick={clearAllFilters}
                      style={{ 
                        borderRadius: '25px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      ) : (
        <Row className="g-3 g-md-4 mb-4">
          <Col xs={12}>
            <h5 className="section-title mb-3 d-flex align-items-center">
              <div className="p-2 me-2">
                <FaChartBar />
              </div>
              <span>Audit Progress Analytics</span>
            </h5>
          </Col>
          <Col xs={12}>
            <FilterSection />
          </Col>
          <Col xs={12}>
            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
              <Card.Header className="bg-white border-bottom-0 py-2 py-md-3">
                <div className="d-flex align-items-center justify-content-end">
                  {!isFullscreen && (
                    <button
                      className="btn btn-outline-primary btn-sm d-flex align-items-center"
                      onClick={toggleFullscreen}
                      title="Enter Fullscreen"
                    >
                      <FaExpand className="me-1" />
                      <span className="d-none d-sm-inline">Fullscreen</span>
                      <span className="d-sm-none">Full</span>
                    </button>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="p-2 p-md-3">
                {currentData.length > 0 ? (
                  <div>
                    <div style={{ 
                      width: '100%', 
                      height: window.innerWidth < 768 ? 350 : window.innerWidth < 1024 ? 380 : 400 
                    }}>
                      <ResponsiveContainer>
                        <BarChart 
                          data={currentData} 
                          margin={{ 
                            top: 30, 
                            right: window.innerWidth < 768 ? 15 : 40, 
                            left: window.innerWidth < 768 ? 15 : 40, 
                            bottom: 50 
                          }}
                          style={{ fontFamily: '"Segoe UI", "Roboto", sans-serif' }}
                        >
                          <CartesianGrid 
                            strokeDasharray="2 4" 
                            vertical={false} 
                            stroke="rgba(0,0,0,0.06)"
                            strokeWidth={1}
                          />
                          <XAxis 
                            dataKey={viewMode === 'department' ? "departmentName" : "branchName"} 
                            angle={window.innerWidth < 768 ? -90 : -45} 
                            textAnchor="end"
                            height={50}
                            tick={{ 
                              fontSize: window.innerWidth < 768 ? 9 : 12, 
                              fill: '#495057',
                              fontWeight: '500',
                              fontFamily: '"Segoe UI", "Roboto", sans-serif'
                            }}
                            tickLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                            label={{ 
                              value: viewMode === 'department' ? 'Departments' : 'Branches', 
                              position: 'insideBottom', 
                              offset: -20,
                              style: { 
                                textAnchor: 'middle', 
                                fontSize: window.innerWidth < 768 ? '11px' : '13px', 
                                fill: '#495057',
                                fontWeight: '600'
                              }
                            }}
                          />
                          <YAxis 
                            domain={[0, 100]}
                            tickFormatter={(value) => `${value}%`}
                            tick={{ 
                              fontSize: window.innerWidth < 768 ? 9 : 12, 
                              fill: '#495057',
                              fontWeight: '500',
                              fontFamily: '"Segoe UI", "Roboto", sans-serif'
                            }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                            tickLine={{ stroke: 'rgba(0,0,0,0.1)', strokeWidth: 1 }}
                            label={{ 
                              value: 'Progress (%)', 
                              angle: -90, 
                              position: 'insideLeft',
                              style: { 
                                textAnchor: 'middle', 
                                fontSize: window.innerWidth < 768 ? '11px' : '13px', 
                                fill: '#495057',
                                fontWeight: '600'
                              }
                            }}
                          />
                          <RechartsTooltip content={<CustomTooltip />} />
                          <Bar 
                            dataKey="progress" 
                            stackId="a"
                            fill="#28a745"
                            name="Progress"
                            barSize={window.innerWidth < 768 ? 40 : 80}
                            radius={[0, 0, 0, 0]}
                            animationDuration={1800}
                            animationEasing="ease-out"
                          >
                            <LabelList 
                              dataKey="progress" 
                              position="center" 
                              formatter={(value) => value > 8 ? `${value}%` : ''}
                              style={{ 
                                fontSize: window.innerWidth < 768 ? '9px' : '11px', 
                                fill: 'white', 
                                fontWeight: '700',
                                fontFamily: '"Segoe UI", "Roboto", sans-serif',
                                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                              }} 
                            />
                          </Bar>
                          <Bar 
                            dataKey="remaining" 
                            stackId="a"
                            fill="#dc3545"
                            name="Remaining"
                            barSize={window.innerWidth < 768 ? 40 : 80}
                            radius={[4, 4, 0, 0]}
                            animationDuration={1800}
                            animationEasing="ease-out"
                          >
                            <LabelList 
                              dataKey="remaining" 
                              position="center" 
                              formatter={(value) => value > 8 ? `${value}%` : ''}
                              style={{ 
                                fontSize: window.innerWidth < 768 ? '9px' : '11px', 
                                fill: 'white', 
                                fontWeight: '700',
                                fontFamily: '"Segoe UI", "Roboto", sans-serif',
                                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                              }} 
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <ChartLegend />
                  </div>
                ) : (
                  <div className="text-center py-3 py-md-5">
                    <div className="mb-3 mb-md-4 bg-light p-3 p-md-4 rounded-circle d-inline-flex align-items-center justify-content-center" style={{
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: '2px solid #dee2e6'
                    }}>
                      <FaChartBar size={window.innerWidth < 768 ? 24 : 36} className="text-secondary" />
                    </div>
                    <h6 className="text-muted mb-2 mb-md-3" style={{ fontWeight: '600' }}>No audit progress data available</h6>
                    <p className="text-muted small" style={{ lineHeight: '1.6' }}>
                      {hasActiveFilters
                        ? 'No data found for the selected filters. Try adjusting your filters to see more data.'
                        : 'Complete some audits to see progress analytics here.'}
                    </p>
                    {hasActiveFilters && (
                      <button 
                        className="btn btn-outline-primary btn-sm mt-2 mt-md-3 px-3 px-md-4 py-2"
                        onClick={clearAllFilters}
                        style={{ 
                          borderRadius: '25px',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
}

export default AuditProgress;